import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Send, 
  FileText, 
  User, 
  Mail,
  MapPin, 
  AlertCircle,
  CheckCircle,
  Copy,
  Calendar
} from 'lucide-react';
import { usePlainte, TYPES_PLAINTE, LOCALITES } from '../hooks/usePlaintes';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const FormCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2.5rem;
  box-shadow: var(--shadow-lg);
  margin-top: -2rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: var(--font-secondary);
  font-size: 1.5rem;
  color: var(--text-dark);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: var(--primary-blue);
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-dark);
  font-size: 0.95rem;

  span {
    color: var(--secondary-red);
  }
`;

const Input = styled.input`
  padding: 0.875rem 1rem;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }

  &::placeholder {
    color: var(--text-light);
  }
`;

const Select = styled.select`
  padding: 0.875rem 1rem;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;
  background: var(--white);
  cursor: pointer;
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const TextArea = styled.textarea`
  padding: 0.875rem 1rem;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  font-family: inherit;
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }

  &::placeholder {
    color: var(--text-light);
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  color: var(--white);
  border-radius: var(--radius-md);
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 38, 100, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessCard = styled.div`
  background: linear-gradient(135deg, #059669, #10b981);
  color: var(--white);
  border-radius: var(--radius-xl);
  padding: 3rem 2rem;
  text-align: center;
  margin-top: -2rem;
  position: relative;
  z-index: 2;

  svg {
    margin-bottom: 1rem;
  }

  h2 {
    font-family: var(--font-secondary);
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  p {
    opacity: 0.9;
    margin-bottom: 1.5rem;
  }
`;

const ReferenceBox = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border: 2px dashed rgba(255, 255, 255, 0.5);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  margin: 1.5rem 0;

  .label {
    font-size: 0.9rem;
    opacity: 0.8;
    margin-bottom: 0.5rem;
  }

  .reference {
    font-family: monospace;
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: 2px;
  }
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: var(--white);
  color: var(--primary-blue);
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: var(--accent-gold);
  }
`;

const InfoBox = styled.div`
  background: #DBEAFE;
  border-left: 4px solid var(--primary-blue);
  padding: 1rem 1.5rem;
  border-radius: var(--radius-md);
  margin-bottom: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  svg {
    color: var(--primary-blue);
    flex-shrink: 0;
    margin-top: 0.2rem;
  }

  p {
    color: #1E40AF;
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const DepotPlainte = () => {
  const { createPlainte } = usePlainte();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reference, setReference] = useState('');
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    localite: '',
    type: '',
    objet: '',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const result = await createPlainte(formData);
    
    if (result.success) {
      setReference(result.reference);
      setSubmitted(true);
    } else {
      alert('Erreur: ' + result.error);
    }
    
    setIsSubmitting(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(reference);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (submitted) {
    return (
      (<PageContainer>
        <Hero>
          <HeroContent>
            <HeroTitle>Réclamation déposée</HeroTitle>
            <HeroSubtitle>
                          Votre Réclamation a été enregistrée avec succès
                        </HeroSubtitle>
          </HeroContent>
        </Hero>
        <Container>
          <SuccessCard>
            <CheckCircle size={64} />
            <h2>Merci pour votre confiance</h2>
            <p>
                          Votre Réclamation a été transmise à la Médiature de la République. 
                          Elle sera traitée dans les meilleurs délais.
                        </p>
            
            <ReferenceBox>
              <div className="label">Votre numéro de référence</div>
              <div className="reference">{reference}</div>
            </ReferenceBox>
            
            <p>Conservez ce numéro pour suivre l'évolution de votre dossier.</p>
            
            <CopyButton onClick={handleCopy}>
              {copied ? 'Copié !' : <><Copy size={18} /> Copier la référence</>}
            </CopyButton>
          </SuccessCard>
        </Container>
      </PageContainer>)
    );
  }

  return (
    (<PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>Déposer une Réclamation</HeroTitle>
          <HeroSubtitle>
                      La Médiature de la République est à votre écoute. 
                      Déposez votre Réclamation en toute confidentialité.
                    </HeroSubtitle>
        </HeroContent>
      </Hero>
      <Container>
        <FormCard>
          <InfoBox>
            <AlertCircle size={24} />
            <p>
              Les champs marqués d'un astérisque (*) sont obligatoires. 
              Après dépôt, vous recevrez un numéro de référence pour suivre votre dossier.
              Toutes les informations seront traitées de manière strictement confidentielle.
            </p>
          </InfoBox>

          <form onSubmit={handleSubmit}>
            <SectionTitle>
              <User size={24} />
              Informations personnelles
            </SectionTitle>

            <FormGrid>
              <FormGroup>
                <Label>Prénom <span>*</span></Label>
                <Input
                  type="text"
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  placeholder="Votre prénom"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Nom <span>*</span></Label>
                <Input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="Votre nom"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  <Mail size={16} style={{ display: 'inline', marginRight: '0.3rem' }} />
                  Email
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="votre@email.com"
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  <MapPin size={16} style={{ display: 'inline', marginRight: '0.3rem' }} />
                  Localité <span>*</span>
                </Label>
                <Select
                  value={formData.localite}
                  onChange={(e) => setFormData({ ...formData, localite: e.target.value })}
                  required
                >
                  <option value="">Sélectionnez une localité</option>
                  {LOCALITES.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Type de Réclamation <span>*</span></Label>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="">Sélectionnez le type</option>
                  {TYPES_PLAINTE.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </Select>
              </FormGroup>
            </FormGrid>

            <SectionTitle>
              <FileText size={24} />
                          Détails de la Réclamation
                        </SectionTitle>

            <FormGrid>
              <FormGroup className="full-width">
                <Label>Objet de la Réclamation <span>*</span></Label>
                <Input
                  type="text"
                  value={formData.objet}
                  onChange={(e) => setFormData({ ...formData, objet: e.target.value })}
                  placeholder="Résumez brièvement l'objet de votre Réclamation"
                  required
                />
              </FormGroup>

              <FormGroup className="full-width">
                <Label>Description détaillée <span>*</span></Label>
                <TextArea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder='Décrivez votre Réclamation en détail. Incluez les dates, les personnes concernées, et tous les éléments pertinents...'
                  required
                />
              </FormGroup>
            </FormGrid>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                'Envoi en cours...'
              ) : (
                <>
                  <Send size={20} />
                                  Déposer ma Réclamation
                                </>
              )}
            </SubmitButton>
          </form>
        </FormCard>
      </Container>
    </PageContainer>)
  );
};

export default DepotPlainte;
